import _ from 'lodash';
import { findChildren, findItem, findItemIndex } from '@/trendData/itemStore.utilities';
import { TERMINAL_STATUSES } from '@/trendData/itemStoreBase.constants';
import { removeColor, updateColorUsage } from '@/trendData/itemStoreColor.utilities';
import {
  CHILD_CLONED_PROPERTIES,
  ITEM_CUSTOMIZATIONS,
  ITEM_DATA_STATUS,
  ITEM_TYPES,
} from '@/trendData/trendData.constants';
import type { Item } from '@/trendData/itemStore.utilities';
import type {
  BaseItemActions,
  BaseItemState,
  CustomizationPayload,
  DataStatusPayload,
} from '@/trendData/itemStoreBase.types';

// ---------------------------------------------------------------------------
// Draft-mutating helpers — operate on a mutable `{ items: Item[] }` slice.
// These work on both Immer drafts (Zustand stores) and plain objects (tests).
// ---------------------------------------------------------------------------

/**
 * Sets the data status fields on an item in-place. Clears `data` for terminal
 * statuses to avoid stale data being displayed after a failure.
 */
export function setDataStatusOnDraft(
  draft: Pick<BaseItemState, 'items'>,
  status: string,
  payload: DataStatusPayload,
): void {
  const index = findItemIndex(draft.items, payload.id);
  if (index === -1) return;

  const item = draft.items[index];
  Object.assign(item, {
    dataStatus: status,
    statusMessage: _.escape(payload.message),
    warningCount: _.isNumber(payload.warningCount) ? payload.warningCount : 0,
    warningLogs: _.isArray(payload.warningLogs) ? payload.warningLogs : [],
    errorType: payload.errorType,
    errorCategory: payload.errorCategory,
    timingInformation: payload.timingInformation,
    meterInformation: payload.meterInformation,
    isSharedRequest: payload.isSharedRequest,
  });

  if (_.includes(TERMINAL_STATUSES, status) && 'data' in item) {
    item.data = [];
  }
}

/**
 * Removes the given items from `draft.items` in-place and frees their colors.
 * Resolved by id; items not found in the draft are silently skipped.
 */
export function removeItemsOnDraft(draft: Pick<BaseItemState, 'items'>, payload: { items: Item[] }): void {
  const toRemove = _.chain(payload.items)
    .map('id')
    .map((id) => findItem(draft.items, id))
    .compact()
    .value();

  toRemove.forEach((item) => {
    const index = findItemIndex(draft.items, item.id);
    if (index >= 0) {
      draft.items.splice(index, 1);
      removeColor(item.color);
    }
  });
}

/**
 * Sets or unsets each key from `payload` (excluding `id` and `includeChildren`)
 * on the matching item in-place.
 */
export function setPropertiesOnDraft(
  draft: Pick<BaseItemState, 'items'>,
  payload: { id: string; includeChildren?: boolean } & Record<string, unknown>,
): void {
  const item = findItem(draft.items, payload.id);
  if (!item) return;

  _.forEach(_.omit(payload, ['id', 'includeChildren']), (value, key) => {
    if (_.isNil(value)) {
      delete item[key];
    } else {
      item[key] = value;
    }
  });
}

/**
 * Sets the `color` field on the matching item and propagates it to children whose
 * `CHILD_CLONED_PROPERTIES` include `'color'`. Adjusts the color-use counts.
 */
export function setColorOnDraft(
  draft: Pick<BaseItemState, 'items'>,
  payload: { id: string; color: string | undefined },
): void {
  const item = findItem(draft.items, payload.id);
  if (item) {
    removeColor(item.color);
    item.color = payload.color;
    updateColorUsage(payload.color);
  }

  findChildren(draft.items, payload.id).forEach((child) => {
    if (_.includes(CHILD_CLONED_PROPERTIES[child.childType as keyof typeof CHILD_CLONED_PROPERTIES], 'color')) {
      child.color = payload.color;
    }
  });
}

/**
 * Sets the `selected` field on the matching item and propagates it to children
 * whose `CHILD_CLONED_PROPERTIES` include `'selected'`.
 */
export function setSelectedOnDraft(
  draft: Pick<BaseItemState, 'items'>,
  payload: { item: Item; selected: boolean },
): void {
  const item = findItem(draft.items, payload.item.id);
  if (item) {
    item.selected = payload.selected;
  }

  findChildren(draft.items, payload.item.id).forEach((child) => {
    if (_.includes(CHILD_CLONED_PROPERTIES[child.childType as keyof typeof CHILD_CLONED_PROPERTIES], 'selected')) {
      child.selected = payload.selected;
    }
  });
}

/**
 * Applies customization properties (lane, axisAlign, etc.) to each matching
 * item and propagates cloned customizations to children.
 */
export function setCustomizationsOnDraft(
  draft: Pick<BaseItemState, 'items'>,
  payload: { items: CustomizationPayload[] },
): void {
  payload.items.forEach((itemPayload) => {
    const item = findItem(draft.items, itemPayload.id);
    if (item) {
      const customizations = _.pick(itemPayload, [
        ...ITEM_CUSTOMIZATIONS[item.itemType as keyof typeof ITEM_CUSTOMIZATIONS],
      ]);
      Object.assign(item, customizations);
    }

    let childPayload: CustomizationPayload = itemPayload;
    let omitProps = ['itemType'];
    if (itemPayload.itemType === ITEM_TYPES.CONDITION) {
      omitProps = _.concat(omitProps, ['lane', 'axisAlign']);
    }
    childPayload = _.omit(childPayload, omitProps) as CustomizationPayload;

    if (_.size(childPayload) <= 1) return;

    findChildren(draft.items, itemPayload.id).forEach((child) => {
      const childCustomizations = _.pick(
        childPayload,
        _.intersection(
          ITEM_CUSTOMIZATIONS[child.itemType as keyof typeof ITEM_CUSTOMIZATIONS] as string[],
          CHILD_CLONED_PROPERTIES[child.childType as keyof typeof CHILD_CLONED_PROPERTIES] as string[],
        ),
      );
      if (!_.isEmpty(childCustomizations)) {
        Object.assign(child, childCustomizations);
      }
    });
  });
}

/**
 * Replaces the `id` of each item listed in `payload.swaps` (keyed by old id,
 * valued by new id) in-place. Used during asset-swap operations.
 */
export function swapItemsOnDraft(
  draft: Pick<BaseItemState, 'items'>,
  payload: { swaps: Record<string, string> },
): void {
  _.forEach(payload.swaps, (swappedInId, swappedOutId) => {
    const item = findItem(draft.items, swappedOutId);
    if (item) {
      item.id = swappedInId;
    }
  });
}

// ---------------------------------------------------------------------------
// Zustand action factory
// ---------------------------------------------------------------------------

/**
 * Creates the standard set of item-store actions for a Zustand store.
 *
 * Usage in a store factory:
 * ```ts
 * const myActionsFactory: ActionsFactory<MyState, MyActions> = (set) => ({
 *   ...createBaseItemActions(set),
 *   // store-specific actions...
 * });
 * ```
 *
 * @param set - Immer-enabled setter from `createStore`
 */
export function createBaseItemActions<S extends BaseItemState>(
  set: (recipe: (draft: S) => void) => void,
): BaseItemActions {
  return {
    removeItems: (payload) =>
      set((draft) => {
        removeItemsOnDraft(draft, payload);
      }),

    setProperties: (payload) =>
      set((draft) => {
        setPropertiesOnDraft(draft, payload);
        if (payload.includeChildren) {
          findChildren(draft.items, payload.id).forEach((child) => {
            setPropertiesOnDraft(draft, { ...payload, id: child.id });
          });
        }
      }),

    setCustomizations: (payload) =>
      set((draft) => {
        setCustomizationsOnDraft(draft, payload);
      }),

    setStatistics: (payload) =>
      set((draft) => {
        const item = findItem(draft.items, payload.id);
        if (item) item.statistics = payload.statistics;
      }),

    setProgress: (payload) =>
      set((draft) => {
        const item = findItem(draft.items, payload.id);
        if (!item) return;
        item.progress = payload.progress;
        if (_.has(payload, 'timingInformation')) item.timingInformation = payload.timingInformation;
        if (_.has(payload, 'meterInformation')) item.meterInformation = payload.meterInformation;
      }),

    setColor: (payload) =>
      set((draft) => {
        setColorOnDraft(draft, payload);
      }),

    setSelectedCapsules: (payload) =>
      set((draft) => {
        payload.capsules.forEach((capsule) => {
          setSelectedOnDraft(draft, { item: capsule, selected: payload.selected });
        });
      }),

    setSelected: (payload) =>
      set((draft) => {
        setSelectedOnDraft(draft, payload);
      }),

    swapItems: (payload) =>
      set((draft) => {
        swapItemsOnDraft(draft, payload);
      }),

    updateLastFetchRequest: (payload) =>
      set((draft) => {
        const item = findItem(draft.items, payload.id);
        if (item) item.lastFetchRequest = new Date().toISOString();
      }),

    setDataStatusPresent: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.PRESENT, payload);
      }),

    setDataStatusLoading: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.LOADING, payload);
      }),

    setDataStatusFailure: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.FAILURE, payload);
      }),

    setDataStatusRedacted: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.REDACTED, payload);
      }),

    setDataStatusCanceled: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.CANCELED, payload);
      }),

    setDataStatusCanceledRetries: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.CANCELED_RETRIES, payload);
      }),

    setDataStatusHiddenFromTrend: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.HIDDEN_FROM_TREND, payload);
      }),

    setDataStatusNotRequired: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.NOT_REQUIRED, payload);
      }),

    setDataStatusAborted: (payload) =>
      set((draft) => {
        setDataStatusOnDraft(draft, ITEM_DATA_STATUS.ABORTED, payload);
      }),
  };
}
